import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, LifeBuoy, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supportAPI } from '../../services/api';
import { toast } from 'react-toastify';
import supportKnowledgeBase from '../../data/supportKnowledgeBase';

// Case-insensitive keyword-overlap scorer against the role-filtered KB — no
// LLM, entirely client-side, matches the Phase 1 decision to keep this free
// and instant rather than wired to a paid API.
const findBestMatch = (queryText, entries) => {
  const q = queryText.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const entry of entries) {
    const score = entry.keywords.reduce((s, kw) => (q.includes(kw.toLowerCase()) ? s + 1 : s), 0);
    if (score > bestScore) { bestScore = score; best = entry; }
  }
  return bestScore > 0 ? best : null;
};

const statusLabel = { pending: 'Pending', in_progress: 'In Progress', resolved: 'Resolved' };
const statusColor = { pending: 'bg-amber-400', in_progress: 'bg-blue-400', resolved: 'bg-green-400' };

let nextMsgId = 1;

export default function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [checkedActive, setCheckedActive] = useState(false);
  const [mode, setMode] = useState('bot'); // 'bot' | 'ticket'
  const [botMessages, setBotMessages] = useState([]);
  const [input, setInput] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [escalateSubject, setEscalateSubject] = useState('');
  const [escalateText, setEscalateText] = useState('');
  const [ticket, setTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticketInput, setTicketInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const role = user?.role;
  const entries = supportKnowledgeBase.filter(e => e.roles.includes(role));

  const refreshTicket = useCallback(async (ticketId) => {
    try {
      const res = await supportAPI.getTicket(ticketId);
      setTicket(res.data.data.ticket);
      setTicketMessages(res.data.data.messages);
    } catch {
      // transient poll failure — try again on the next tick, no need to surface a toast
    }
  }, []);

  // Server is the source of truth for "do I have an open conversation" —
  // checked once when the panel is first opened, not on every page load.
  useEffect(() => {
    if (open && !checkedActive) {
      setCheckedActive(true);
      supportAPI.getActiveTicket().then(res => {
        if (res.data.data) {
          setMode('ticket');
          refreshTicket(res.data.data.id);
        } else if (botMessages.length === 0) {
          setBotMessages([{ id: nextMsgId++, from: 'bot', text: "Hi! Ask me how to do something, or tap \"Talk to a human\" any time." }]);
        }
      }).catch(() => {
        if (botMessages.length === 0) setBotMessages([{ id: nextMsgId++, from: 'bot', text: "Hi! Ask me how to do something, or tap \"Talk to a human\" any time." }]);
      });
    }
  }, [open, checkedActive, botMessages.length, refreshTicket]);

  // Polling — the Phase 2 "live chat" transport, per the earlier decision
  // to avoid an unverified WebSocket path through the current IIS proxy.
  useEffect(() => {
    if (!open || mode !== 'ticket' || !ticket || ticket.status === 'resolved') return undefined;
    const interval = setInterval(() => refreshTicket(ticket.id), 5000);
    return () => clearInterval(interval);
  }, [open, mode, ticket, refreshTicket]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [botMessages, ticketMessages, escalating]);

  if (role !== 'admin' && role !== 'teacher') return null;

  const pushBotMessage = (msg) => setBotMessages(m => [...m, { id: nextMsgId++, ...msg }]);

  const handleSend = (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    pushBotMessage({ from: 'user', text });
    setInput('');
    const match = findBestMatch(text, entries);
    if (match) {
      pushBotMessage({ from: 'bot', text: match.answer });
    } else {
      pushBotMessage({ from: 'bot', text: "I couldn't find an answer for that.", offerHuman: true });
    }
  };

  const startEscalation = () => {
    setEscalateSubject('');
    setEscalateText(input);
    setEscalating(true);
  };

  const submitEscalation = async (e) => {
    e.preventDefault();
    if (!escalateText.trim()) { toast.error('Enter a message for the support team'); return; }
    setSending(true);
    try {
      const res = await supportAPI.requestHuman(escalateSubject.trim(), escalateText.trim());
      setEscalating(false);
      setMode('ticket');
      await refreshTicket(res.data.data.id);
      toast.success(`Sent — reference ${res.data.data.ticket_number}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send — please try again');
    } finally {
      setSending(false);
    }
  };

  const sendTicketReply = async (e) => {
    e.preventDefault();
    const text = ticketInput.trim();
    if (!text || !ticket) return;
    setSending(true);
    try {
      await supportAPI.replyToTicket(ticket.id, text);
      setTicketInput('');
      await refreshTicket(ticket.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const startNewConversation = () => {
    setMode('bot');
    setTicket(null);
    setTicketMessages([]);
    setBotMessages([{ id: nextMsgId++, from: 'bot', text: "Hi! Ask me how to do something, or tap \"Talk to a human\" any time." }]);
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center hover:bg-indigo-700 transition-colors"
        aria-label="Open support chat"
      >
        {open ? <X size={22}/> : <MessageCircle size={22}/>}
      </button>

      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[90vw] h-[480px] max-h-[75vh] bg-white rounded-xl border border-gray-200 shadow-2xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-indigo-600 text-white">
            <div className="flex items-center gap-2">
              <MessageCircle size={16}/>
              <span className="font-semibold text-sm">
                {mode === 'ticket' && ticket ? ticket.ticket_number : 'Support Assistant'}
              </span>
              {mode === 'ticket' && ticket && (
                <span className="flex items-center gap-1 text-[10px] bg-white/15 px-1.5 py-0.5 rounded">
                  <span className={`w-1.5 h-1.5 rounded-full ${statusColor[ticket.status]}`}/>
                  {statusLabel[ticket.status]}
                </span>
              )}
            </div>
            {mode === 'bot' && (
              <button onClick={startEscalation} className="flex items-center gap-1 text-xs bg-white/15 hover:bg-white/25 px-2 py-1 rounded">
                <LifeBuoy size={12}/>Talk to a human
              </button>
            )}
          </div>

          {mode === 'bot' ? (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                {botMessages.map(m => (
                  <div key={m.id} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] text-sm rounded-lg px-3 py-2 ${m.from === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>
                      {m.text}
                      {m.offerHuman && (
                        <button onClick={startEscalation} className="block mt-2 text-xs font-medium text-indigo-600 hover:underline">
                          Talk to a human →
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {botMessages.length <= 1 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {entries.slice(0, 5).map(e => (
                      <button key={e.id}
                        onClick={() => { pushBotMessage({ from: 'user', text: e.question }); pushBotMessage({ from: 'bot', text: e.answer }); }}
                        className="text-xs bg-white border border-gray-200 rounded-full px-2.5 py-1 text-gray-600 hover:border-indigo-300 hover:text-indigo-600">
                        {e.question}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={bottomRef}/>
              </div>

              {escalating ? (
                <form onSubmit={submitEscalation} className="p-3 border-t border-gray-100 space-y-2">
                  <input value={escalateSubject} onChange={e => setEscalateSubject(e.target.value)}
                    placeholder="Subject (optional)"
                    className="w-full text-sm border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                  <textarea value={escalateText} onChange={e => setEscalateText(e.target.value)} rows={3}
                    placeholder="Describe what you need help with..."
                    className="w-full text-sm border border-gray-300 rounded-lg px-2.5 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"/>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setEscalating(false)} className="flex-1 text-xs font-medium py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50">
                      Cancel
                    </button>
                    <button type="submit" disabled={sending} className="flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
                      <Send size={12}/>{sending ? 'Sending...' : 'Send to Support Team'}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSend} className="p-3 border-t border-gray-100 flex gap-2">
                  <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask a question..."
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                  <button type="submit" className="w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700">
                    <Send size={14}/>
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
                {ticket?.subject && (
                  <p className="text-xs text-gray-400 text-center pb-1">{ticket.subject}</p>
                )}
                {ticketMessages.map(m => (
                  <div key={m.id} className={`flex ${m.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] text-sm rounded-lg px-3 py-2 ${m.sender_type === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-200 text-gray-700'}`}>
                      {m.message}
                    </div>
                  </div>
                ))}
                {ticket?.status === 'resolved' && (
                  <div className="flex flex-col items-center gap-2 py-3 text-center">
                    <span className="flex items-center gap-1.5 text-xs font-medium text-green-700"><CheckCircle2 size={14}/>This ticket has been resolved</span>
                    <button onClick={startNewConversation} className="text-xs font-medium text-indigo-600 hover:underline">
                      Start New Conversation
                    </button>
                  </div>
                )}
                <div ref={bottomRef}/>
              </div>

              {ticket?.status !== 'resolved' && (
                <form onSubmit={sendTicketReply} className="p-3 border-t border-gray-100 flex gap-2">
                  <input value={ticketInput} onChange={e => setTicketInput(e.target.value)} placeholder="Type a message..."
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
                  <button type="submit" disabled={sending} className="w-9 h-9 flex items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60">
                    <Send size={14}/>
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
